/**
 * Curated QA Management Component
 * Admin interface for managing curated Q&A pairs (golden answers)
 */

import { useState, useEffect } from "react";
import { orchestratorApi } from "../api/orchestrator";

interface QAPair {
  id: number;
  question: string;
  answer: string;
  category: string;
  subcategory?: string;
  tags?: string[];
  keywords?: string[];
  alternative_questions?: string[];
  priority: number;
  quality_score: number;
  usage_count: number;
  success_rate: number;
  is_active: boolean;
  language: string;
  created_at: string;
  updated_at: string;
}

interface QACategory {
  id: number;
  category_key: string;
  display_name: string;
  description?: string;
  icon_emoji?: string;
  color_code?: string;
  is_active: boolean;
}

interface QAStats {
  total_qas: number;
  active_qas: number;
  total_usage: number;
  avg_rating: number;
  by_category: Array<{ category: string; count: number }>;
}

export default function CuratedQAManagement() {
  const [activeTab, setActiveTab] = useState<"browse" | "create" | "categories">("browse");
  const [qaPairs, setQAPairs] = useState<QAPair[]>([]);
  const [categories, setCategories] = useState<QACategory[]>([]);
  const [stats, setStats] = useState<QAStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string>("");
  const [showInactive, setShowInactive] = useState(false);
  const [editingQA, setEditingQA] = useState<QAPair | null>(null);

  // Form state for create/edit
  const [formData, setFormData] = useState({
    question: "",
    answer: "",
    category: "",
    subcategory: "",
    tags: "",
    keywords: "",
    alternative_questions: "",
    priority: 50,
    language: "english",
    is_active: true,
  });

  useEffect(() => {
    loadQAPairs();
    loadCategories();
    loadStats();
  }, []);

  useEffect(() => {
    loadQAPairs();
  }, [selectedCategory, showInactive]);

  const loadQAPairs = async () => {
    setLoading(true);
    try {
      const filters: any = {};
      if (selectedCategory) filters.category = selectedCategory;
      if (showInactive) filters.is_active = false;

      const response = await orchestratorApi.getCuratedQAPairs(filters);
      if (response.success) {
        setQAPairs(response.pairs || []);
      }
    } catch (error) {
      console.error("Failed to load QA pairs:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const response = await orchestratorApi.getCuratedQACategories();
      if (response.success) {
        setCategories(response.categories || []);
      }
    } catch (error) {
      console.error("Failed to load categories:", error);
    }
  };

  const loadStats = async () => {
    try {
      const response = await orchestratorApi.getCuratedQAStats();
      if (response.success) {
        setStats(response.stats);
      }
    } catch (error) {
      console.error("Failed to load stats:", error);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      loadQAPairs();
      return;
    }

    setLoading(true);
    try {
      const response = await orchestratorApi.searchCuratedQA(searchQuery, { limit: 50 });
      if (response.success && response.results?.results) {
        setQAPairs(response.results.results);
      } else if (response.success && response.results) {
        // Handle case where results is the array directly
        setQAPairs(Array.isArray(response.results) ? response.results : []);
      }
    } catch (error) {
      console.error("Search failed:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateQA = async () => {
    try {
      const data = {
        question: formData.question,
        answer: formData.answer,
        category: formData.category,
        subcategory: formData.subcategory || undefined,
        tags: formData.tags ? formData.tags.split(",").map(t => t.trim()) : [],
        keywords: formData.keywords ? formData.keywords.split(",").map(k => k.trim()) : [],
        alternative_questions: formData.alternative_questions ? formData.alternative_questions.split("|").map(q => q.trim()) : [],
        priority: parseInt(formData.priority.toString()),
        language: formData.language,
        is_active: formData.is_active,
      };

      const response = await orchestratorApi.createCuratedQAPair(data);

      if (response.success) {
        alert("QA pair created successfully!");
        resetForm();
        loadQAPairs();
        loadStats();
      } else {
        alert("Failed to create QA pair: " + (response.error || "Unknown error"));
      }
    } catch (error) {
      alert("Error creating QA pair: " + error);
    }
  };

  const handleUpdateQA = async () => {
    if (!editingQA) return;

    try {
      const data = {
        question: formData.question,
        answer: formData.answer,
        category: formData.category,
        subcategory: formData.subcategory || undefined,
        tags: formData.tags ? formData.tags.split(",").map(t => t.trim()) : [],
        keywords: formData.keywords ? formData.keywords.split(",").map(k => k.trim()) : [],
        alternative_questions: formData.alternative_questions ? formData.alternative_questions.split("|").map(q => q.trim()) : [],
        priority: parseInt(formData.priority.toString()),
        language: formData.language,
        is_active: formData.is_active,
      };

      const response = await orchestratorApi.updateCuratedQAPair(editingQA.id, data);

      if (response.success) {
        alert("QA pair updated successfully!");
        resetForm();
        setEditingQA(null);
        loadQAPairs();
      } else {
        alert("Failed to update QA pair: " + (response.error || "Unknown error"));
      }
    } catch (error) {
      alert("Error updating QA pair: " + error);
    }
  };

  const handleDeleteQA = async (id: number) => {
    if (!confirm("Are you sure you want to delete this QA pair?")) return;

    try {
      const response = await orchestratorApi.deleteCuratedQAPair(id);

      if (response.success) {
        alert("QA pair deleted successfully!");
        loadQAPairs();
        loadStats();
      } else {
        alert("Failed to delete QA pair: " + (response.error || "Unknown error"));
      }
    } catch (error) {
      alert("Error deleting QA pair: " + error);
    }
  };

  const handleEditQA = (qa: QAPair) => {
    setEditingQA(qa);
    setFormData({
      question: qa.question,
      answer: qa.answer,
      category: qa.category,
      subcategory: qa.subcategory || "",
      tags: (qa.tags || []).join(", "),
      keywords: (qa.keywords || []).join(", "),
      alternative_questions: (qa.alternative_questions || []).join(" | "),
      priority: qa.priority,
      language: qa.language,
      is_active: qa.is_active,
    });
    setActiveTab("create");
  };

  const resetForm = () => {
    setFormData({
      question: "",
      answer: "",
      category: "",
      subcategory: "",
      tags: "",
      keywords: "",
      alternative_questions: "",
      priority: 50,
      language: "english",
      is_active: true,
    });
    setEditingQA(null);
  };

  const getCategoryInfo = (categoryKey: string) => {
    return categories.find(c => c.category_key === categoryKey);
  };

  return (
    <div className="curated-qa-management">
      <h2>📚 Curated Q&A Management</h2>

      {/* Statistics */}
      {stats && (
        <div className="stats-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "16px", marginBottom: "24px" }}>
          <div className="stat-card" style={{ padding: "16px", background: "#f8f9fa", borderRadius: "8px", border: "1px solid #dee2e6" }}>
            <div style={{ fontSize: "24px", fontWeight: "bold", color: "#3b82f6" }}>{stats.total_qas}</div>
            <div style={{ fontSize: "14px", color: "#6c757d" }}>Total Q&A Pairs</div>
          </div>
          <div className="stat-card" style={{ padding: "16px", background: "#f8f9fa", borderRadius: "8px", border: "1px solid #dee2e6" }}>
            <div style={{ fontSize: "24px", fontWeight: "bold", color: "#10b981" }}>{stats.active_qas}</div>
            <div style={{ fontSize: "14px", color: "#6c757d" }}>Active</div>
          </div>
          <div className="stat-card" style={{ padding: "16px", background: "#f8f9fa", borderRadius: "8px", border: "1px solid #dee2e6" }}>
            <div style={{ fontSize: "24px", fontWeight: "bold", color: "#f59e0b" }}>{stats.total_usage}</div>
            <div style={{ fontSize: "14px", color: "#6c757d" }}>Total Usage</div>
          </div>
          <div className="stat-card" style={{ padding: "16px", background: "#f8f9fa", borderRadius: "8px", border: "1px solid #dee2e6" }}>
            <div style={{ fontSize: "24px", fontWeight: "bold", color: "#8b5cf6" }}>
              {stats.avg_rating ? stats.avg_rating.toFixed(1) : "N/A"}
            </div>
            <div style={{ fontSize: "14px", color: "#6c757d" }}>Avg Rating</div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="tabs" style={{ marginBottom: "20px", borderBottom: "1px solid #dee2e6" }}>
        <button
          className={`tab-button ${activeTab === "browse" ? "active" : ""}`}
          onClick={() => setActiveTab("browse")}
          style={{
            padding: "10px 20px",
            background: "none",
            border: "none",
            borderBottom: activeTab === "browse" ? "2px solid #3b82f6" : "none",
            cursor: "pointer",
            fontWeight: activeTab === "browse" ? "bold" : "normal",
          }}
        >
          Browse Q&A
        </button>
        <button
          className={`tab-button ${activeTab === "create" ? "active" : ""}`}
          onClick={() => setActiveTab("create")}
          style={{
            padding: "10px 20px",
            background: "none",
            border: "none",
            borderBottom: activeTab === "create" ? "2px solid #3b82f6" : "none",
            cursor: "pointer",
            fontWeight: activeTab === "create" ? "bold" : "normal",
          }}
        >
          {editingQA ? "Edit Q&A" : "Create New"}
        </button>
        <button
          className={`tab-button ${activeTab === "categories" ? "active" : ""}`}
          onClick={() => setActiveTab("categories")}
          style={{
            padding: "10px 20px",
            background: "none",
            border: "none",
            borderBottom: activeTab === "categories" ? "2px solid #3b82f6" : "none",
            cursor: "pointer",
            fontWeight: activeTab === "categories" ? "bold" : "normal",
          }}
        >
          Categories
        </button>
      </div>

      {/* Browse Tab */}
      {activeTab === "browse" && (
        <div>
          {/* Filters */}
          <div style={{ display: "flex", gap: "12px", marginBottom: "20px", alignItems: "center" }}>
            <input
              type="text"
              placeholder="Search Q&A..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => e.key === "Enter" && handleSearch()}
              style={{ padding: "8px 12px", border: "1px solid #ccc", borderRadius: "4px", flex: 1 }}
            />
            <button onClick={handleSearch} style={{ padding: "8px 16px", background: "#3b82f6", color: "white", border: "none", borderRadius: "4px", cursor: "pointer" }}>
              Search
            </button>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              style={{ padding: "8px 12px", border: "1px solid #ccc", borderRadius: "4px" }}
            >
              <option value="">All Categories</option>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.category_key}>
                  {cat.icon_emoji} {cat.display_name}
                </option>
              ))}
            </select>
            <label style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <input
                type="checkbox"
                checked={showInactive}
                onChange={(e) => setShowInactive(e.target.checked)}
              />
              Show Inactive
            </label>
            {searchQuery && (
              <button onClick={() => { setSearchQuery(""); loadQAPairs(); }} style={{ padding: "8px 16px", background: "#6c757d", color: "white", border: "none", borderRadius: "4px", cursor: "pointer" }}>
                Clear
              </button>
            )}
          </div>

          {/* Q&A List */}
          {loading ? (
            <div style={{ textAlign: "center", padding: "40px" }}>Loading...</div>
          ) : qaPairs.length === 0 ? (
            <div style={{ textAlign: "center", padding: "40px", color: "#6c757d" }}>
              No Q&A pairs found. Create one to get started!
            </div>
          ) : (
            <div className="qa-list">
              {qaPairs.map((qa) => {
                const catInfo = getCategoryInfo(qa.category);
                return (
                  <div
                    key={qa.id}
                    style={{
                      border: "1px solid #dee2e6",
                      borderRadius: "8px",
                      padding: "16px",
                      marginBottom: "12px",
                      background: qa.is_active ? "#fff" : "#f8f9fa",
                      opacity: qa.is_active ? 1 : 0.7,
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
                          {catInfo?.icon_emoji && <span>{catInfo.icon_emoji}</span>}
                          <span style={{
                            padding: "2px 8px",
                            borderRadius: "4px",
                            fontSize: "12px",
                            background: catInfo?.color_code || "#6c757d",
                            color: "white",
                          }}>
                            {catInfo?.display_name || qa.category}
                          </span>
                          {!qa.is_active && <span style={{ padding: "2px 8px", borderRadius: "4px", fontSize: "12px", background: "#dc3545", color: "white" }}>Inactive</span>}
                          <span style={{ fontSize: "12px", color: "#6c757d" }}>Priority: {qa.priority}</span>
                        </div>
                        <h4 style={{ margin: "0 0 8px 0", fontSize: "16px" }}>{qa.question}</h4>
                        <p style={{ margin: "0 0 8px 0", color: "#495057", lineHeight: "1.5" }}>{qa.answer}</p>
                        <div style={{ fontSize: "12px", color: "#6c757d", display: "flex", gap: "16px", flexWrap: "wrap" }}>
                          <span>Used {qa.usage_count} times</span>
                          <span>Success: {Math.round((qa.success_rate || 0) * 100)}%</span>
                          {(qa.tags || []).length > 0 && <span>Tags: {(qa.tags || []).join(", ")}</span>}
                        </div>
                      </div>
                      <div style={{ display: "flex", gap: "8px", marginLeft: "16px" }}>
                        <button
                          onClick={() => handleEditQA(qa)}
                          style={{ padding: "6px 12px", background: "#3b82f6", color: "white", border: "none", borderRadius: "4px", cursor: "pointer" }}
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteQA(qa.id)}
                          style={{ padding: "6px 12px", background: "#dc3545", color: "white", border: "none", borderRadius: "4px", cursor: "pointer" }}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Create/Edit Tab */}
      {activeTab === "create" && (
        <div>
          <div style={{ maxWidth: "800px", margin: "0 auto" }}>
            <h3 style={{ marginBottom: "20px" }}>{editingQA ? "Edit Q&A Pair" : "Create New Q&A Pair"}</h3>

            <form onSubmit={(e) => { e.preventDefault(); editingQA ? handleUpdateQA() : handleCreateQA(); }}>
              <div style={{ marginBottom: "16px" }}>
                <label style={{ display: "block", marginBottom: "4px", fontWeight: "bold" }}>Question *</label>
                <input
                  type="text"
                  required
                  value={formData.question}
                  onChange={(e) => setFormData({ ...formData, question: e.target.value })}
                  style={{ width: "100%", padding: "8px", border: "1px solid #ccc", borderRadius: "4px" }}
                  placeholder="e.g., What is the minimum salary for Employment Pass?"
                />
              </div>

              <div style={{ marginBottom: "16px" }}>
                <label style={{ display: "block", marginBottom: "4px", fontWeight: "bold" }}>Answer *</label>
                <textarea
                  required
                  value={formData.answer}
                  onChange={(e) => setFormData({ ...formData, answer: e.target.value })}
                  style={{ width: "100%", padding: "8px", border: "1px solid #ccc", borderRadius: "4px", minHeight: "120px" }}
                  placeholder="Provide a clear, concise answer..."
                />
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px", marginBottom: "16px" }}>
                <div>
                  <label style={{ display: "block", marginBottom: "4px", fontWeight: "bold" }}>Category *</label>
                  <select
                    required
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    style={{ width: "100%", padding: "8px", border: "1px solid #ccc", borderRadius: "4px" }}
                  >
                    <option value="">Select category...</option>
                    {categories.map((cat) => (
                      <option key={cat.id} value={cat.category_key}>
                        {cat.icon_emoji} {cat.display_name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label style={{ display: "block", marginBottom: "4px", fontWeight: "bold" }}>Subcategory</label>
                  <input
                    type="text"
                    value={formData.subcategory}
                    onChange={(e) => setFormData({ ...formData, subcategory: e.target.value })}
                    style={{ width: "100%", padding: "8px", border: "1px solid #ccc", borderRadius: "4px" }}
                    placeholder="e.g., Eligibility, Documents"
                  />
                </div>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px", marginBottom: "16px" }}>
                <div>
                  <label style={{ display: "block", marginBottom: "4px", fontWeight: "bold" }}>Priority (1-100)</label>
                  <input
                    type="number"
                    min="1"
                    max="100"
                    value={formData.priority}
                    onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) })}
                    style={{ width: "100%", padding: "8px", border: "1px solid #ccc", borderRadius: "4px" }}
                  />
                </div>

                <div>
                  <label style={{ display: "block", marginBottom: "4px", fontWeight: "bold" }}>Language</label>
                  <select
                    value={formData.language}
                    onChange={(e) => setFormData({ ...formData, language: e.target.value })}
                    style={{ width: "100%", padding: "8px", border: "1px solid #ccc", borderRadius: "4px" }}
                  >
                    <option value="english">English</option>
                    <option value="chinese">Chinese</option>
                    <option value="malay">Malay</option>
                    <option value="tamil">Tamil</option>
                  </select>
                </div>
              </div>

              <div style={{ marginBottom: "16px" }}>
                <label style={{ display: "block", marginBottom: "4px", fontWeight: "bold" }}>Tags (comma-separated)</label>
                <input
                  type="text"
                  value={formData.tags}
                  onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                  style={{ width: "100%", padding: "8px", border: "1px solid #ccc", borderRadius: "4px" }}
                  placeholder="e.g., employment, pass, salary"
                />
              </div>

              <div style={{ marginBottom: "16px" }}>
                <label style={{ display: "block", marginBottom: "4px", fontWeight: "bold" }}>Keywords (comma-separated)</label>
                <input
                  type="text"
                  value={formData.keywords}
                  onChange={(e) => setFormData({ ...formData, keywords: e.target.value })}
                  style={{ width: "100%", padding: "8px", border: "1px solid #ccc", borderRadius: "4px" }}
                  placeholder="e.g., work pass, employment pass, EP"
                />
              </div>

              <div style={{ marginBottom: "16px" }}>
                <label style={{ display: "block", marginBottom: "4px", fontWeight: "bold" }}>Alternative Questions (separated by |)</label>
                <input
                  type="text"
                  value={formData.alternative_questions}
                  onChange={(e) => setFormData({ ...formData, alternative_questions: e.target.value })}
                  style={{ width: "100%", padding: "8px", border: "1px solid #ccc", borderRadius: "4px" }}
                  placeholder="e.g., EP minimum salary? | Employment Pass salary requirement?"
                />
              </div>

              <div style={{ marginBottom: "16px" }}>
                <label style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  />
                  Active (will be used in searches)
                </label>
              </div>

              <div style={{ display: "flex", gap: "12px" }}>
                <button
                  type="submit"
                  style={{ padding: "10px 24px", background: "#3b82f6", color: "white", border: "none", borderRadius: "4px", cursor: "pointer" }}
                >
                  {editingQA ? "Update Q&A" : "Create Q&A"}
                </button>
                {(editingQA || formData.question || formData.answer) && (
                  <button
                    type="button"
                    onClick={resetForm}
                    style={{ padding: "10px 24px", background: "#6c757d", color: "white", border: "none", borderRadius: "4px", cursor: "pointer" }}
                  >
                    Cancel
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Categories Tab */}
      {activeTab === "categories" && (
        <div>
          <h3 style={{ marginBottom: "20px" }}>Categories</h3>
          <div className="categories-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: "16px" }}>
            {categories.map((cat) => (
              <div
                key={cat.id}
                style={{
                  border: "1px solid #dee2e6",
                  borderRadius: "8px",
                  padding: "16px",
                  background: cat.is_active ? "#fff" : "#f8f9fa",
                  opacity: cat.is_active ? 1 : 0.6,
                }}
              >
                <div style={{ fontSize: "24px", marginBottom: "8px" }}>{cat.icon_emoji}</div>
                <h4 style={{ margin: "0 0 8px 0" }}>{cat.display_name}</h4>
                <p style={{ margin: "0 0 8px 0", fontSize: "14px", color: "#6c757d" }}>{cat.description}</p>
                <div style={{ fontSize: "12px", color: "#6c757d" }}>
                  Key: {cat.category_key}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
